import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  Divider,
  alpha,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  CircularProgress,
  Chip,
  Stack,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useQuery } from "react-query";
import moment from "moment";
import { QK } from "../../base/qk";
import { getCandidateWeeklyHistory } from "../../api/services/scheduling";
import { formatTimeRangeToAmPm } from "../../base/FormatTime";
import { AccessTime, DirectionsRun, LocationOn, Person, Timer, Work } from "@mui/icons-material";

const EmployeeHistoryModal = ({ show, handleClose, employeeId }) => {
  const theme = useTheme();

  const {
    data: employeeHistory,
    isLoading,
    isError,
  } = useQuery(
    [QK.candidateWeeklyHistory, employeeId],
    () => getCandidateWeeklyHistory(employeeId),
    {
      enabled: !!employeeId,
    }
  );

  const transformData = (historyData) => {
    if (!historyData) {
      return [];
    }

    const scheduleData = historyData.schedule_by_day || historyData;

    if (!Array.isArray(scheduleData)) {
      return [];
    }

    return scheduleData.map((dayData) => {
      const { day, assignments } = dayData;
      const hourlyAssignments = [...Array(24)].map(() => ({
        className: "",
        label: "",
        tooltip: null,
        fillPercentage: 0,
      }));

      if (assignments && Array.isArray(assignments)) {
        assignments.forEach((assignment) => {
          const startTime = moment(assignment.hour_from, "HH:mm:ss");
          const endTime = moment(assignment.hour_to, "HH:mm:ss");
          const startHour = startTime.hour();
          const endHour = endTime.hour();
          const startMinute = startTime.minute();
          const endMinute = endTime.minute();

          const totalMinutes = endTime.diff(startTime, "minutes");
          const fullHours = Math.floor(totalMinutes / 60);
          const remainingMinutes = totalMinutes % 60;

          const clientName = assignment.client || "Unknown Client";
          const visitName = assignment.visit_name || "";
          const run = assignment.run || "";

          const formattedDuration = fullHours
            ? `${fullHours}h ${remainingMinutes > 0 ? `${remainingMinutes}m` : ""}`
            : `${remainingMinutes}m`;

          const tooltipContent = {
            client: clientName,
            visit: visitName,
            run: run,
            scheduled: formatTimeRangeToAmPm(
              assignment.hour_from,
              assignment.hour_to
            ),
            duration: formattedDuration,
          };

          for (let hour = startHour; hour <= endHour; hour++) {
            let fillPercentage = 100;

            if (hour === startHour) {
              fillPercentage = ((60 - startMinute) / 60) * 100;
            } else if (hour === endHour) {
              fillPercentage = (endMinute / 60) * 100;
            }

            hourlyAssignments[hour] = {
              className: "bg-success text-white",
              label: hour === startHour ? (hour < 12 ? "AM" : "PM") : "",
              tooltip: tooltipContent,
              fillPercentage,
            };
          }
        });
      }

      return { day, hourlyAssignments };
    });
  };

  const transformedData = employeeHistory ? transformData(employeeHistory) : [];

  const hours = [...Array(24).keys()];

  const getHourLabel = (hour) => {
    const displayHour = hour % 12 === 0 ? 12 : hour % 12;
    const ampm = hour < 12 ? "AM" : "PM";
    return `${displayHour} ${ampm}`;
  };

  const renderTooltipContent = (tooltipData) => (
    <Box sx={{ p: 1.5, minWidth: 200 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: '6px',
            bgcolor: alpha(theme.palette.primary.main, 0.08),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Work sx={{ fontSize: 16, color: theme.palette.primary.main }} />
        </Box>
        <Typography variant="subtitle2" fontWeight={600} color="#1A1A2E">
          Assignment Details
        </Typography>
      </Box>
      <Divider sx={{ mb: 1.5 }} />
      <Stack spacing={1.5}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
          <Person sx={{ fontSize: 16, color: '#94A3B8', mt: 0.2 }} />
          <Box>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.6rem', fontWeight: 500 }}>
              Client
            </Typography>
            <Typography variant="body2" fontWeight={500} color="#1A1A2E">
              {tooltipData.client}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
          <LocationOn sx={{ fontSize: 16, color: '#94A3B8', mt: 0.2 }} />
          <Box>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.6rem', fontWeight: 500 }}>
              Visit
            </Typography>
            <Typography variant="body2" fontWeight={500} color="#1A1A2E">
              {tooltipData.visit}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
          <DirectionsRun sx={{ fontSize: 16, color: '#94A3B8', mt: 0.2 }} />
          <Box>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.6rem', fontWeight: 500 }}>
              Run
            </Typography>
            <Typography variant="body2" fontWeight={500} color="#1A1A2E">
              {tooltipData.run || 'Not assigned'}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
          <AccessTime sx={{ fontSize: 16, color: '#94A3B8', mt: 0.2 }} />
          <Box>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.6rem', fontWeight: 500 }}>
              Scheduled
            </Typography>
            <Typography variant="body2" fontWeight={500} color="#1A1A2E">
              {tooltipData.scheduled}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
          <Timer sx={{ fontSize: 16, color: '#94A3B8', mt: 0.2 }} />
          <Box>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.6rem', fontWeight: 500 }}>
              Duration
            </Typography>
            <Typography variant="body2" fontWeight={500} color="#1A1A2E">
              {tooltipData.duration}
            </Typography>
          </Box>
        </Box>
      </Stack>
    </Box>
  );

  return (
    <Dialog
      open={show}
      onClose={handleClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          boxShadow: "0px 24px 48px -12px rgba(16, 24, 40, 0.25)",
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle sx={{ p: 0 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2.5,
            pb: 1.5,
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "10px",
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography variant="h6" sx={{ color: theme.palette.primary.main, fontWeight: 700 }}>
                  📊
                </Typography>
              </Box>
              <Typography variant="h6" fontWeight={600} sx={{ color: "#1A1A2E" }}>
                {employeeHistory?.candidate_name || "Employee"}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 3, ml: 7 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">Maximum Hour:</Typography>
                <Chip
                  label={`${employeeHistory?.maximum_weekly_hours || 0}h`}
                  size="small"
                  sx={{
                    ml: 1,
                    height: 20,
                    fontSize: "0.65rem",
                    fontWeight: 600,
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    color: theme.palette.primary.main,
                  }}
                />
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Weekly Assigned:</Typography>
                <Chip
                  label={employeeHistory?.total_assigned_hours || "0h 0m"}
                  size="small"
                  sx={{
                    ml: 1,
                    height: 20,
                    fontSize: "0.65rem",
                    fontWeight: 600,
                    bgcolor: "#D1FAE5",
                    color: "#039855",
                  }}
                />
              </Box>
            </Box>
          </Box>
          <IconButton
            onClick={handleClose}
            size="small"
            sx={{
              color: "#9CA3AF",
              "&:hover": { bgcolor: "#F3F4F6", color: "#4B5563" },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider sx={{ opacity: 0.4 }} />
      </DialogTitle>

      <DialogContent sx={{ p: 2.5, pt: 2 }}>
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        ) : isError ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography color="error">Error loading history.</Typography>
          </Box>
        ) : (
          <Box>
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{
                border: "1px solid #E2E8F0",
                borderRadius: "8px",
                overflow: "auto",
                maxHeight: "500px",
              }}
            >
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        bgcolor: "#F8FAFC",
                        minWidth: 120,
                        position: "sticky",
                        left: 0,
                        zIndex: 3,
                      }}
                    >
                      Day
                    </TableCell>
                    {hours.map((hour) => (
                      <TableCell
                        key={hour}
                        align="center"
                        sx={{
                          fontWeight: 600,
                          bgcolor: "#F8FAFC",
                          minWidth: 40,
                          padding: "4px 2px",
                          fontSize: "0.6rem",
                          color: "#475467",
                        }}
                      >
                        {getHourLabel(hour)}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {transformedData.length > 0 ? (
                    transformedData.map((dayData, index) => (
                      <TableRow key={index}>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            bgcolor: "#FAFBFC",
                            position: "sticky",
                            left: 0,
                            zIndex: 2,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {dayData.day}
                        </TableCell>
                        {dayData.hourlyAssignments.map((cell, hour) => (
                          <TableCell
                            key={hour}
                            align="center"
                            sx={{
                              padding: "2px",
                              position: "relative",
                              background:
                                cell.fillPercentage > 0
                                  ? `linear-gradient(to right, ${alpha(theme.palette.success.main, 0.7)} ${cell.fillPercentage}%, transparent ${cell.fillPercentage}%)`
                                  : "transparent",
                              color: cell.fillPercentage > 50 ? "white" : "black",
                              cursor: cell.tooltip ? "pointer" : "default",
                              minWidth: 36,
                              height: 36,
                            }}
                          >
                            {cell.tooltip ? (
                              <Tooltip
                                title={renderTooltipContent(cell.tooltip)}
                                placement="top"
                                arrow
                                PopperProps={{
                                  sx: {
                                    "& .MuiTooltip-tooltip": {
                                      bgcolor: "white",
                                      color: "#1A1A2E",
                                      boxShadow: "0px 8px 24px rgba(0,0,0,0.12)",
                                      borderRadius: "8px",
                                      padding: 0,
                                      maxWidth: 280,
                                      border: "1px solid #E2E8F0",
                                    },
                                    "& .MuiTooltip-arrow": {
                                      color: "white",
                                    },
                                  },
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    width: "100%",
                                    height: "100%",
                                    minHeight: 32,
                                    fontSize: "0.55rem",
                                    fontWeight: 600,
                                  }}
                                >
                                  {cell.label}
                                </Box>
                              </Tooltip>
                            ) : (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  width: "100%",
                                  height: "100%",
                                  minHeight: 32,
                                  fontSize: "0.55rem",
                                  fontWeight: 600,
                                }}
                              >
                                {cell.label}
                              </Box>
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={25} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">No data available</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ textAlign: "center", mt: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Hover over colored cells to view assignment details
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2.5, pt: 0 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{
            textTransform: "none",
            fontWeight: 500,
            borderRadius: "10px",
            borderColor: "#D0D5DD",
            color: "#344054",
            px: 3,
            "&:hover": {
              bgcolor: "#F9FAFB",
              borderColor: "#98A2B3",
            },
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmployeeHistoryModal;